import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtKpi, toneCls } from "./kpiFormat";

export const BY_SERVICE_KPIS = new Set(["active_members", "class_packs", "attendance_selected", "avg_visits_per_member"]);
export const byService = (r) => r?.components?.by_service;

/** Every service seen across the given results, newest week's order first. */
export function serviceList(results) {
  const seen = new Map();
  [...results].reverse().forEach((r) => (byService(r) || []).forEach((x) => { if (!seen.has(x.id)) seen.set(x.id, x.name); }));
  return [...seen].map(([id, name]) => ({ id, name }));
}

const hitFor = (r, sid) => byService(r)?.find((x) => x.id === sid);
const cellValue = (r, sid, unit) => {
  if (!byService(r)) return null;
  const hit = hitFor(r, sid);
  if (!hit) return unit === "ratio" ? "N/A" : fmtKpi(0, unit);
  return hit.value === null || hit.value === undefined ? "N/A" : fmtKpi(hit.value, unit);
};
const ratioTitle = (r, sid) => {
  const hit = hitFor(r, sid);
  if (!hit || hit.numerator === undefined) return undefined;
  const base = `${hit.numerator} visits ÷ ${hit.denominator} participants`;
  return hit.target != null ? `${base} · target ${hit.target}` : base;
};

export const ExpandToggle = ({ open, onToggle, testId }) => (
  <button type="button" onClick={onToggle} data-testid={testId} aria-expanded={open} title={open ? "Hide services" : "Show by service"}
    className="mr-1 inline-flex h-5 w-5 items-center justify-center rounded text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-800">
    <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-90")} />
  </button>
);

const SvcLink = ({ onClick, children, testId, className }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={cn("text-left underline decoration-dotted decoration-zinc-300 underline-offset-4 transition-colors hover:text-blue-700 hover:decoration-blue-400", className)}>
    {children}
  </button>
);

export const GridServiceRows = ({ row, weeks, selected, onSelect, onDrill }) => serviceList(weeks.map((w) => w.results?.[row.id])).map((s) => (
  <tr key={s.id} className="bg-zinc-50/60" data-testid={`kpi-grid-svc-${row.id}-${s.id}`}>
    <td className="sticky left-0 z-10 bg-zinc-50 px-4 py-1.5 pl-10 text-xs text-zinc-500">↳ {s.name}</td>
    {weeks.map((w) => {
      const r = w.results?.[row.id];
      const v = cellValue(r, s.id, row.unit);
      const tid = `kpi-cell-svc-${row.id}-${s.id}-${w.week_start}`;
      return (
        <td key={w.week_start} onClick={() => onSelect(w.week_start)}
          className={cn("px-3 py-1.5 font-mono-num text-xs border-l border-zinc-100 cursor-pointer", selected === w.week_start && "bg-blue-50/60")}>
          {v === null ? <span className="text-zinc-300" data-testid={tid} title="Not in this snapshot — refresh the draft">—</span> : (
            <SvcLink testId={tid} className={["good", "bad"].includes(hitFor(r, s.id)?.status) ? toneCls(hitFor(r, s.id).status) : "text-zinc-600"}
              onClick={(e) => { e.stopPropagation(); onDrill(w.week_start, row, s); }}>
              <span title={row.unit === "ratio" ? ratioTitle(r, s.id) : "Show the clients behind this number"}>{v}</span>
            </SvcLink>
          )}
        </td>
      );
    })}
  </tr>
));

export const DetailServiceRows = ({ def, r, cmp, onDrill }) => (byService(r) || []).map((x) => {
  const prev = cmp?.by_service?.[x.id];
  return (
    <tr key={x.id} className="bg-zinc-50/60 align-top" data-testid={`kpi-detail-svc-${def.id}-${x.id}`}>
      <td className="py-1.5 pr-3 pl-8 text-xs text-zinc-500">
        ↳ <SvcLink onClick={() => onDrill({ id: x.id, name: x.name })} testId={`kpi-detail-svc-drill-${def.id}-${x.id}`}>{x.name}</SvcLink>
      </td>
      <td className={cn("py-1.5 pr-3 font-mono-num text-xs whitespace-nowrap", x.status ? toneCls(x.status) : "text-zinc-800")} data-testid={`kpi-detail-svc-${def.id}-${x.id}-value`}>
        {x.value === null || x.value === undefined ? <span className="italic text-zinc-400">N/A</span> : fmtKpi(x.value, def.unit)}
      </td>
      <td className="py-1.5 pr-3 font-mono-num text-xs text-zinc-500 whitespace-nowrap" data-testid={`kpi-detail-svc-${def.id}-${x.id}-prev`}>
        {cmp?.by_service === undefined ? "—" : prev === undefined ? (def.unit === "ratio" ? "—" : fmtKpi(0, def.unit)) : prev === null ? "N/A" : fmtKpi(prev, def.unit)}
      </td>
      <td className="py-1.5 pr-3 text-xs text-zinc-500" colSpan={2}>{def.unit === "ratio" ? ratioTitle(r, x.id) : ""}</td>
    </tr>
  );
});
